/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/set-state-in-effect */
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import { LAST_ACTIVE_CONVERSATION_KEY } from '@/sections/insights/aiAssistant/constants/keys';
import type { SidebarTab } from '@/sections/insights/aiAssistant/types/conversation.types';
import type { FailedMessage } from '@/sections/insights/aiAssistant/utils/failedMessage';
import {
  getTenantScopedValue,
  removeTenantScopedValue,
  setTenantScopedValue,
} from '@/utils/storage/tenantScoping.util';

interface HistorySetters {
  setMessageHistory: Dispatch<SetStateAction<string[]>>;
  setHistoryIndex: Dispatch<SetStateAction<number>>;
  setSavedMessage: Dispatch<SetStateAction<string>>;
}

interface MessagingSetters {
  setFailedMessage: Dispatch<SetStateAction<FailedMessage | null>>;
  setIsWaitingForResponse: Dispatch<SetStateAction<boolean>>;
}

interface UseConversationSwitchProps {
  conversationId: string | null;
  // Existence + fetch-state of the active conversation, from
  // useGetConversationById in AIAssistant.tsx. `conversationExists` is
  // `data !== undefined`; `hasFetchedConversation` is that hook's
  // `hasFetchedData` (NOT `isFetched`) — true only after the by-id query has
  // produced at least one page response, so it distinguishes "fetched and
  // empty" (dead conversation → self-heal) from "still loading / disabled /
  // just cleared by queryClient.clear() during impersonation" (wait).
  // Optional so callers/tests that don't wire the by-id lookup simply never
  // self-heal (both default to false → the guard below can never be satisfied).
  conversationExists?: boolean;
  hasFetchedConversation?: boolean;
  setActiveTab: Dispatch<SetStateAction<SidebarTab>>;
  // Reconciles a stale hunt view on a real conversationId transition (e.g.
  // browser Back/Forward restoring ?conversationId=). Keyed on an ACTUAL id
  // change so it never runs on the transient intermediate render.
  setIsHuntView: (isHuntView: boolean) => void;
  isAIDisabledButBypassed: boolean;
  disableAIInference: boolean;
  summonMessage: string | null;
  messages: any[];
  messagesLoading: boolean;
  modeChangeRef: MutableRefObject<'auto' | null>;
  historySetters: HistorySetters;
  messagingSetters: MessagingSetters;
}

export function useConversationSwitch({
  conversationId,
  conversationExists = false,
  hasFetchedConversation = false,
  setActiveTab,
  setIsHuntView,
  isAIDisabledButBypassed,
  disableAIInference,
  summonMessage,
  messages,
  messagesLoading,
  modeChangeRef,
  historySetters: { setMessageHistory, setHistoryIndex, setSavedMessage },
  messagingSetters: { setFailedMessage, setIsWaitingForResponse },
}: UseConversationSwitchProps) {
  const [, setSearchParams] = useSearchParams();
  const [previousConversationId, setPreviousConversationId] = useState<
    string | null
  >(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  // Provenance: the conversationId RESTORED from storage on mount. Ids created
  // via the send/summon path never pass through the mount restore effect below,
  // so they are never marked provisional and the self-heal effect never touches
  // them — protecting the WebSocket create window in useMessageSending where a
  // freshly-created id legitimately has no server record yet.
  const restoredConversationIdRef = useRef<string | null>(null);

  // When AI inference is disabled, clear any active conversation to prevent
  // getting stuck in a "Loading conversation..." loop that never resolves
  useEffect(() => {
    if (isAIDisabledButBypassed && conversationId) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('conversationId');
          return newParams;
        },
        { replace: true }
      );
      setIsLoadingConversation(false);
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [isAIDisabledButBypassed, conversationId, setSearchParams]);

  // Restore last active conversation from tenant-scoped localStorage on mount
  useEffect(() => {
    if (!conversationId && !summonMessage && !disableAIInference) {
      const lastConversationId = getTenantScopedValue<string>(
        LAST_ACTIVE_CONVERSATION_KEY
      );
      const UUID_REGEX =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (lastConversationId && UUID_REGEX.test(lastConversationId)) {
        // Mark this id provisional so the self-heal effect can clear it if it
        // turns out not to exist (see below). Only restored ids are marked.
        restoredConversationIdRef.current = lastConversationId;
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', lastConversationId);
            return newParams;
          },
          { replace: true }
        );
      }
    }
  }, []); // Mount-only: reads URL params once to restore session. Post-mount flag changes handled by isAIDisabledButBypassed effect above.

  // Self-heal a dead RESTORED conversation. When the id we restored from storage
  // resolves to a conversation that does not exist (the by-id query FETCHED and
  // returned empty — this is false during loading and after the impersonation
  // queryClient.clear(), so it naturally waits out those windows, and it never
  // sees 403/499/5xx as "empty"), clear the stale URL param AND the
  // tenant-scoped storage key atomically — mirroring the isAIDisabledButBypassed
  // clear above — so the user lands on the welcome view instead of a stranded
  // "Loading conversation…" that 404s on POST /planner. The provenance guard
  // ensures this never fires for a client-created id, and the mount-only restore
  // effect above cannot re-inject the cleared id, so there is no re-poison loop.
  useEffect(() => {
    if (
      conversationId &&
      conversationId === restoredConversationIdRef.current &&
      hasFetchedConversation &&
      !conversationExists &&
      !isLoadingConversation
    ) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('conversationId');
          return newParams;
        },
        { replace: true }
      );
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
      restoredConversationIdRef.current = null;
    }
  }, [
    conversationId,
    conversationExists,
    hasFetchedConversation,
    isLoadingConversation,
    setSearchParams,
  ]);

  // Track conversation switching and clear states to prevent flash.
  // Mode/agent are NO LONGER restored here — they derive from conversationId in
  // AIAssistant's adjust-during-render block (deriveConvState). This effect owns
  // only the non-mode side effects (loading flag, tab, flash/history clearing).
  useEffect(() => {
    if (conversationId !== previousConversationId) {
      if (conversationId) {
        setIsLoadingConversation(true);
        modeChangeRef.current = null;
        // A restored sidebar conversation is always a chat conversation, so it
        // always lands on the conversations tab (never hunts).
        setActiveTab('conversations');
        // Reconcile a stale hunt view: a real transition to a truthy conversationId
        // (e.g. browser Back/Forward restoring ?conversationId= without going through
        // handleSelectConversation) means a chat is active, which is never the hunt
        // view. This effect keys on an ACTUAL conversationId change, so it never runs
        // on the transient intermediate render of handleModeChange('auto') (id→null),
        // and so cannot clobber a freshly-set hunt view. See aiAssistant/CLAUDE.md
        // "The isHuntView tearing hazard".
        setIsHuntView(false);
      } else if (!modeChangeRef.current) {
        // Cleared to a fresh chat (not the hunt view). When modeChangeRef is
        // 'auto', handleModeChange already set the hunts tab + isHuntView, so
        // leave the tab alone here.
        setActiveTab('conversations');
      }
      // Clear any pending states to prevent content flash when switching conversations
      setFailedMessage(null);
      setIsWaitingForResponse(false);
      // Reset history navigation state when switching conversations
      setMessageHistory([]);
      setHistoryIndex(-1);
      setSavedMessage('');
    }
    setPreviousConversationId(conversationId);
  }, [conversationId, previousConversationId]);

  // Stop loading when messages arrive or when no conversation is selected
  // Also populate message history from loaded conversation
  useEffect(() => {
    if (
      (messages.length > 0 || !messagesLoading || !conversationId) &&
      isLoadingConversation
    ) {
      setIsLoadingConversation(false);
      if (messages.length > 0) {
        const userMessages = messages
          .filter(msg => msg.role === 'user' && msg.content)
          .map(msg => msg.content);
        if (userMessages.length > 0) {
          setMessageHistory(userMessages);
          setHistoryIndex(-1);
          setSavedMessage('');
        }
      }
    }
  }, [messages.length, messagesLoading, conversationId, isLoadingConversation]);

  // NOTE: The broad "save mode/agent on any change" effect and the
  // agent-unavailable downgrade effect were DELETED. Persistence now happens at
  // the deliberate write sites (applyChatMode / applyActiveAgent /
  // createConversationWorker / useSummon), and the agent-unavailable downgrade
  // is handled reactively by deriveConvState (resolveSelectableMode) in the
  // adjust-during-render block. Removing the broad effect removes the
  // corrupting writer by construction.

  // Persist active conversation to tenant-scoped localStorage for session restore
  useEffect(() => {
    if (conversationId) {
      setTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY, conversationId);
    } else {
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [conversationId]);

  return {
    isLoadingConversation,
  };
}
